"use client";
import { differenceInDays } from "date-fns";
import { useParams, usePathname } from "next/navigation";
import { useAccount } from "queries/account";
import { User } from "queries/current-user/types";
import { usePaymentState } from "queries/entitlements/hooks";
import { useTeam } from "queries/teams";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Query, QueryKey, useQuery } from "react-query";
import { useSession } from "ui/containers/session";
import { canUserEdit } from "ui/lib/helpers";
import { useUserEntitlements } from "ui/lib/useUserEntitlements";
import { clearSentryUser, setSentryUser } from "../lib/sentry-user";
import { getUserPrivileges } from "./getUserPrivileges";
import {
  hasPinnedRoster,
  hasProgrammingGrant,
  hasWeekExportGrant,
  isPinnedRosterCoach,
} from "./programming-grants";
import { getCurrentUser } from "./services";

export const useCurrentUser = (config?: Query<User, Error, User, QueryKey>) => {
  const booted = useRef(false);
  const { session, setSession } = useSession();
  const pathname = usePathname();
  const params = useParams();
  const activeTeamID = useMemo(() => params?.bootcampId, [params]);
  const {
    data: currentUser,
    error,
    isLoading,
    refetch,
  } = useQuery<User, Error>(["/users/me"], getCurrentUser(session as string), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!session,
    ...(config || {}),
  });

  // refetchOnMount: false matches the /users/me and paymentState queries in
  // this hook — without it, every component mounting useCurrentUser (e.g.
  // one per workout-builder row) refired GET /v1/getUserAccount.
  const { data: account } = useAccount(currentUser?.id as string, {
    refetchOnMount: false,
  });
  const { data: activeTeamData } = useTeam(activeTeamID as string);
  const { data: paymentState } = usePaymentState(currentUser?.id as string, {
    enabled: !!currentUser?.id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const activeVendorProduct = useMemo(
    () => findActiveVendorProduct(paymentState as VendorData),
    [paymentState]
  );

  const { data: entitlements, isValidating } = useUserEntitlements(
    currentUser?.id
  );
  const privileges = getUserPrivileges(entitlements?.userEntitlements || []);
  const isAdmin = useMemo(
    () => Boolean(currentUser && currentUser.role === "admin"),
    [currentUser]
  );

  const isCoach = useMemo(
    () => Boolean(currentUser && currentUser.role === "coach"),
    [currentUser]
  );

  const isAdminOrCoach = isAdmin || isCoach;

  const isCoachesTeam = useMemo(() => {
    if (!params) return false;
    if (!isCoach) return false;
    if (!currentUser?.id) return false;

    if (hasPinnedRoster(activeTeamData?.team.id)) {
      return isPinnedRosterCoach(currentUser.id, activeTeamData?.team.id);
    }

    if (!activeTeamData?.teamCoaches) return false;
    const teamCoachIDs = activeTeamData.teamCoaches.map((c) => c.coachUserID);
    return teamCoachIDs.includes(currentUser.id);
  }, [isCoach, currentUser, activeTeamData]);

  const canProgramTeam = useMemo(
    () =>
      isCoachesTeam ||
      (isCoach && hasProgrammingGrant(currentUser?.id, activeTeamID as string)),
    [isCoach, isCoachesTeam, currentUser?.id, activeTeamID]
  );

  const canExportWeek = useMemo(
    () =>
      canProgramTeam ||
      (isCoach && hasWeekExportGrant(currentUser?.id, activeTeamID as string)),
    [canProgramTeam, isCoach, currentUser?.id, activeTeamID]
  );

  const canEditData = useCallback(
    (date) => {
      if (!currentUser) return false;
      if (isCoach && !canProgramTeam) return false;
      if (isAdmin) return true;
      const { id, role } = currentUser;
      return canUserEdit({ id, role, date });
    },
    [isAdmin, currentUser, isCoach, canProgramTeam]
  );

  const trialData = useMemo(() => {
    if (!currentUser) return { trialing: false };
    if (currentUser.membershipStatus !== "trialing") return { trialing: false };
    if (!currentUser.trialExpiration) return { trialing: false };
    return {
      trialing: true,
      daysRemaining: differenceInDays(
        new Date(currentUser.trialExpiration),
        new Date()
      ),
    };
  }, [currentUser]);

  const canceledOrExpired = useMemo(() => {
    if (!currentUser) return false;
    if (!currentUser.trialExpiration) return false;
    if (!["trialing", "canceled"].includes(currentUser.membershipStatus))
      return false;
    if (currentUser.membershipStatus === "canceled") return true;
    return (
      differenceInDays(new Date(currentUser.trialExpiration), new Date()) <= 0
    );
  }, [currentUser]);

  useEffect(() => {
    // Keep the client Sentry scope's user in sync with auth state. The fetch
    // path (getCurrentUserFn) also sets it, but this covers mounts served
    // entirely from the react-query cache and clears identity on logout.
    if (!session) clearSentryUser();
    else if (currentUser?.id) setSentryUser(currentUser.id);
  }, [session, currentUser?.id]);

  useEffect(() => {
    // handle banned users. auto log them out and redirect to home
    if (booted.current) return;
    if (pathname === "/") return;
    if (!!currentUser?.isBanned) {
      setSession();
      booted.current = true;
      setTimeout(() => window.location.reload(), 100);
    }
  }, [currentUser, pathname, setSession]);

  return {
    account,
    activeVendorProduct,
    session,
    isAdminOrCoach,
    isCoachesTeam,
    canProgramTeam,
    canExportWeek,
    trialData,
    canceledOrExpired,
    currentUser: currentUser?.id ? currentUser : undefined,
    isAdmin,
    isCoach,
    error,
    refetch,
    isLoading: isLoading && !currentUser,
    isLoadingEntitlements: isValidating && !entitlements,
    entitlements,
    privileges,
    canEditData,
  };
};

interface Subscription {
  id: string;
  userID: string;
  vendorProductID: string;
  originalPurchasedTSStr: string;
  currentPeriodStartTSStr: string;
  currentPeriodEndTSStr: string;
  currentPeriodType: string;
  status: string;
}

interface VendorProduct {
  id: string;
  vendorName: string;
  productID: string;
}

interface VendorData {
  subscriptions: Subscription[];
  vendorProducts: VendorProduct[];
}

const findActiveVendorProduct = (
  data: VendorData
): VendorProduct | undefined => {
  if (!data?.subscriptions?.length) return undefined;

  // Find the active subscription (include canceling — user still has access until period ends)
  const activeSubscription = data.subscriptions.find(
    (sub) => sub.status === "active" || sub.status === "canceling"
  );

  if (!activeSubscription) {
    return undefined;
  }

  // Use the vendorProductID from the active subscription to find the matching vendor product
  const vendorProduct = data.vendorProducts.find(
    (vp) => vp.id === activeSubscription.vendorProductID
  );

  if (!vendorProduct) {
    return undefined;
  }

  return vendorProduct;
};
